import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getTags } from '../services/tagsService';
import type { TagsRegistry } from '../services/tagsService';

/**
 * Fetches the BE tags registry — the preset chip universe across origins,
 * dietary, and allergens, with labels in the current i18n language.
 *
 * Public endpoint (no auth needed) so this works pre-sign-in for the
 * filter sheet. Keyed on `i18n.language` so toggling locale fires a
 * fresh fetch (BE returns the labels in the new lang). Cached for the
 * session — the registry rarely changes at runtime (AI may append new
 * non-preset entries, but the preset subset is curated and stable).
 *
 * Returns empty arrays per category on error so consumers can render
 * a "loading or unavailable" empty state without crashing.
 */
export function useTagsRegistry() {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'en';

  return useQuery<TagsRegistry>({
    queryKey: ['tags', lang],
    queryFn: getTags,
    // Tags rarely change at runtime; refetch only on lang change (the
    // queryKey flip) or a manual invalidate. `staleTime: Infinity`
    // prevents background refetches every time the hook mounts on a
    // new page.
    staleTime: Infinity,
    // Don't blow up the UI on a fetch failure — the publish form would
    // be unusable. Caller gets empty arrays and degrades.
    placeholderData: { origins: [], dietary: [], allergens: [] },
  });
}
